import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  fetchIntakeHome,
  IntakeAgentWeeklySummaryDto,
  IntakeFlagFilter,
  IntakeHomeResponseDto,
  IntakeTrackingRowDto,
} from '../../api/IntakeHomeApi';
import AutomationActivityFeed from '../../components/AutomationActivityFeed/AutomationActivityFeed';

function formatDateShort(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTimeShort(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function leadSourceLabel(
  source: IntakeTrackingRowDto['leadSource'],
): string {
  switch (source) {
    case 'website_intake':
      return 'Unpaid Website Lead';
    case 'wordpress_form':
      return 'WordPress Form Lead';
    case 'phone_inquiry':
      return 'Phone Inquiry Lead';
    default:
      return 'Lead (No Case Yet)';
  }
}

const ManagerIntakeHomeView: React.FC = () => {
  const [selectedStaff, setSelectedStaff] = useState<string>('all');
  const [selectedFlag, setSelectedFlag] = useState<IntakeFlagFilter>('all');
  const [createdDays, setCreatedDays] = useState<number>(0);
  const [searchInput, setSearchInput] = useState<string>('');
  const [appliedSearch, setAppliedSearch] = useState<string>('');
  const [showDataNotes, setShowDataNotes] = useState<boolean>(true);
  const [showInactiveStaff, setShowInactiveStaff] = useState<boolean>(false);

  const [data, setData] = useState<IntakeHomeResponseDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadIntakeHome = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchIntakeHome({
        staff: selectedStaff,
        flag: selectedFlag,
        search: appliedSearch || undefined,
        createdDays: createdDays > 0 ? createdDays : undefined,
      });
      setData(response);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load Intake Home data',
      );
    } finally {
      setLoading(false);
    }
  }, [selectedStaff, selectedFlag, appliedSearch, createdDays]);

  useEffect(() => {
    loadIntakeHome();
  }, [loadIntakeHome]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchInput.trim());
  };

  const handleResetFilters = () => {
    setSelectedStaff('all');
    setSelectedFlag('all');
    setCreatedDays(0);
    setSearchInput('');
    setAppliedSearch('');
  };

  const rows = data?.rows ?? [];
  const summaries = data?.agentWeeklySummaries ?? [];
  const activeStaffSummaries = summaries.filter(
    (a) => a.hasActivityThisWeek && !a.isUnattributedLine,
  );
  const unattributedSummary = summaries.find((a) => a.isUnattributedLine);
  const inactiveStaffSummaries = summaries.filter(
    (a) => !a.hasActivityThisWeek && !a.isUnattributedLine,
  );

  const renderAgentRow = (
    agent: IntakeAgentWeeklySummaryDto,
    isInactive = false,
  ) => {
    const isSelected =
      selectedStaff.toLowerCase() === agent.agentName.toLowerCase();
    const isUnattributed = Boolean(agent.isUnattributedLine);

    return (
      <TableRow
        key={agent.agentName}
        hover
        selected={isSelected}
        sx={{
          cursor: isUnattributed ? 'default' : 'pointer',
          bgcolor: isUnattributed
            ? 'rgba(0, 0, 0, 0.02)'
            : undefined,
          opacity: isInactive ? 0.65 : 1,
        }}
        onClick={() => {
          if (!isUnattributed) {
            setSelectedStaff(isSelected ? 'all' : agent.agentName);
          }
        }}
      >
        <TableCell sx={{ fontWeight: isUnattributed ? 500 : 600 }}>
          {agent.agentName}
          {isUnattributed && (
            <Chip
              size="small"
              color="default"
              variant="outlined"
              label="Line names / missed"
              sx={{ ml: 1, height: 20, fontSize: '0.68rem' }}
            />
          )}
          {isSelected && (
            <Chip
              size="small"
              color="primary"
              label="Filtered"
              sx={{ ml: 1, height: 20 }}
            />
          )}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 600 }}>
          {agent.callsThisWeek}
        </TableCell>
        <TableCell align="right">
          {agent.outboundCallsThisWeek} out / {agent.inboundCallsThisWeek} in
        </TableCell>
        <TableCell align="right">
          {agent.connectedCallsThisWeek ?? agent.spokeCallsThisWeek ?? 0}
        </TableCell>
        <TableCell align="right">
          {agent.unmatchedCallsThisWeek > 0 ? (
            <Tooltip
              title={`${agent.unmatchedCallsThisWeek} call(s) with prospective clients not yet matched to a case`}
            >
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label={agent.unmatchedCallsThisWeek}
                sx={{ height: 20, fontWeight: 600 }}
              />
            </Tooltip>
          ) : (
            0
          )}
        </TableCell>
        <TableCell align="right">
          {agent.followUpNotesThisWeek}
        </TableCell>
        <TableCell align="right">
          {agent.messagesSentThisWeek}
        </TableCell>
        <TableCell align="right">
          {agent.distinctCasesTouchedThisWeek}
        </TableCell>
        <TableCell align="right">
          {agent.assignedOpenCaseCount}
        </TableCell>
        <TableCell align="right">
          {agent.flaggedNoContactCount > 0 ? (
            <Chip
              size="small"
              color="error"
              label={agent.flaggedNoContactCount}
            />
          ) : (
            0
          )}
        </TableCell>
        <TableCell align="right">
          {agent.flaggedLorUnconfirmedCount > 0 ? (
            <Chip
              size="small"
              color="warning"
              label={agent.flaggedLorUnconfirmedCount}
            />
          ) : (
            0
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Page Header & Department Navigation */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        sx={{ mb: 3, gap: 1.5 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Intake & Follow-Up Home
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track sales agent calls and client follow-ups, LORs sent to courts, and LOR confirmations across all open cases and leads.
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'inline-block',
              mt: 0.75,
              px: 1,
              py: 0.25,
              bgcolor: 'action.hover',
              borderRadius: 1,
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            Scope: Open cases created in the last 60 days, plus open cases where LOR is sent and not yet confirmed (closed cases excluded).
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="outlined" size="small" component={Link} to="/">
            Main Home
          </Button>
          <Button
            variant="outlined"
            size="small"
            component={Link}
            to="/activity-feed"
          >
            Full Activity Feed
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={loadIntakeHome}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* KPI Summary Cards (clickable to filter) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(5, 1fr)',
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Card
          variant="outlined"
          sx={{
            cursor: 'pointer',
            borderColor: selectedFlag === 'all' ? 'primary.main' : undefined,
            borderWidth: selectedFlag === 'all' ? 2 : 1,
          }}
          onClick={() => setSelectedFlag('all')}
        >
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Open Cases{data?.totalLeads ? ` + ${data.totalLeads} Leads` : ''}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {(data?.totalOpenCases ?? 0) + (data?.totalLeads ?? 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Click to show all
            </Typography>
          </CardContent>
        </Card>

        <Card
          variant="outlined"
          sx={{
            cursor: 'pointer',
            borderColor:
              selectedFlag === 'no_contact_2d' ? 'error.main' : undefined,
            borderWidth: selectedFlag === 'no_contact_2d' ? 2 : 1,
            bgcolor:
              (data?.flaggedNoContactCount ?? 0) > 0
                ? 'rgba(211, 47, 47, 0.04)'
                : undefined,
          }}
          onClick={() =>
            setSelectedFlag(
              selectedFlag === 'no_contact_2d' ? 'all' : 'no_contact_2d',
            )
          }
        >
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
              Flagged: LOR Not Sent, No Contact 2+ Days
            </Typography>
            <Typography variant="h4" color="error.main" sx={{ fontWeight: 700 }}>
              {data?.flaggedNoContactCount ?? 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Needs client follow-up
            </Typography>
          </CardContent>
        </Card>

        <Card
          variant="outlined"
          sx={{
            cursor: 'pointer',
            borderColor:
              selectedFlag === 'lor_unconfirmed' ? 'warning.main' : undefined,
            borderWidth: selectedFlag === 'lor_unconfirmed' ? 2 : 1,
            bgcolor:
              (data?.flaggedLorUnconfirmedCount ?? 0) > 0
                ? 'rgba(237, 108, 2, 0.04)'
                : undefined,
          }}
          onClick={() =>
            setSelectedFlag(
              selectedFlag === 'lor_unconfirmed' ? 'all' : 'lor_unconfirmed',
            )
          }
        >
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography
              variant="caption"
              color="warning.dark"
              sx={{ fontWeight: 600 }}
            >
              Flagged: LOR Sent, Awaiting Court Confirmation
            </Typography>
            <Typography
              variant="h4"
              color="warning.dark"
              sx={{ fontWeight: 700 }}
            >
              {data?.flaggedLorUnconfirmedCount ?? 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Awaiting court acknowledgment
            </Typography>
          </CardContent>
        </Card>

        <Card
          variant="outlined"
          sx={{
            cursor: 'pointer',
            borderColor:
              selectedFlag === 'lor_not_sent' ? 'info.main' : undefined,
            borderWidth: selectedFlag === 'lor_not_sent' ? 2 : 1,
          }}
          onClick={() =>
            setSelectedFlag(
              selectedFlag === 'lor_not_sent' ? 'all' : 'lor_not_sent',
            )
          }
        >
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              LOR Not Sent Yet
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {data?.flaggedLorNotSentCount ?? 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Traffic cases pending LOR
            </Typography>
          </CardContent>
        </Card>

        <Card
          variant="outlined"
          sx={{
            cursor: 'pointer',
            borderColor:
              selectedFlag === 'lor_confirmed' ? 'success.main' : undefined,
            borderWidth: selectedFlag === 'lor_confirmed' ? 2 : 1,
          }}
          onClick={() =>
            setSelectedFlag(
              selectedFlag === 'lor_confirmed' ? 'all' : 'lor_confirmed',
            )
          }
        >
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
              LOR Confirmed by Court
            </Typography>
            <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
              {data?.lorConfirmedCount ?? 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Confirmed receipt on file
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Per-Agent Weekly Summary (Calls & Follow-ups This Week) */}
      <Paper elevation={1} sx={{ p: 2.5, mb: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          sx={{ mb: 1.5, gap: 1 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Per-Agent Activity Summary (Past 7 Days)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Calls from Dialpad case notes (<code>UserNote_AuthorName</code>) and follow-up notes/messages over the last 7 days. Click an agent row to filter the case table below.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {data?.unknownNumbersSummary ? (
              <Tooltip
                title={`${data.unknownNumbersSummary.inboundCalls} in / ${data.unknownNumbersSummary.outboundCalls} out (${data.unknownNumbersSummary.spokeCalls} answered, ${data.unknownNumbersSummary.noAnswerCalls} no answer, ${data.unknownNumbersSummary.voicemailCalls} voicemail). Click to view queue.`}
              >
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  clickable
                  component={Link}
                  to="/unmatched-calls"
                  label={`Calls from unknown numbers this week: ${data.unknownNumbersSummary.totalCallsThisWeek} (${data.unknownNumbersSummary.distinctCallersThisWeek} distinct callers)`}
                />
              </Tooltip>
            ) : typeof data?.unmatchedCallsThisWeek === 'number' && data.unmatchedCallsThisWeek > 0 ? (
              <Chip
                size="small"
                color="info"
                variant="outlined"
                clickable
                component={Link}
                to="/unmatched-calls"
                label={`Calls from unknown numbers this week: ${data.unmatchedCallsThisWeek}`}
              />
            ) : null}
            <Button
              size="small"
              variant="text"
              onClick={() => setShowDataNotes((prev) => !prev)}
            >
              {showDataNotes ? 'Hide data notes' : 'What is missing in data?'}
            </Button>
          </Stack>
        </Stack>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Staff / Agent</TableCell>
                <TableCell align="right">Calls This Week</TableCell>
                <TableCell align="right">Outbound / Inbound</TableCell>
                <TableCell align="right">Connected (Spoke)</TableCell>
                <TableCell align="right">Unknown Numbers (Leads)</TableCell>
                <TableCell align="right">Follow-Up Notes</TableCell>
                <TableCell align="right">Messages Sent</TableCell>
                <TableCell align="right">Cases Touched (7d)</TableCell>
                <TableCell align="right">Attributed Open Cases</TableCell>
                <TableCell align="right">Flagged: 2+ Days No Contact</TableCell>
                <TableCell align="right">Flagged: LOR Unconfirmed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summaries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No staff or call activity found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {activeStaffSummaries.map((agent) => renderAgentRow(agent))}
                  {unattributedSummary && renderAgentRow(unattributedSummary)}
                  {inactiveStaffSummaries.length > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={11}
                        sx={{
                          py: 1,
                          bgcolor: 'action.hover',
                          textAlign: 'center',
                        }}
                      >
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setShowInactiveStaff((prev) => !prev)}
                          sx={{ textTransform: 'none', fontWeight: 500 }}
                        >
                          {showInactiveStaff
                            ? `Hide ${inactiveStaffSummaries.length} inactive staff members`
                            : `Show ${inactiveStaffSummaries.length} inactive staff members (0 activity this week)`}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                  {showInactiveStaff &&
                    inactiveStaffSummaries.map((agent) =>
                      renderAgentRow(agent, true),
                    )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Collapse in={showDataNotes}>
          {data?.missingDataDisclosures &&
            data.missingDataDisclosures.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Data Availability & Attribution Notes:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {data.missingDataDisclosures.map((note, idx) => (
                    <li key={idx}>
                      <Typography variant="body2">{note}</Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
        </Collapse>
      </Paper>

      {/* Case & Lead Filter Bar */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
          flexWrap="wrap"
          useFlexGap
        >
          <Box
            component="form"
            onSubmit={handleSearchSubmit}
            sx={{ display: 'flex', gap: 1, flex: 1, minWidth: 240 }}
          >
            <TextField
              fullWidth
              size="small"
              label="Search client name, phone, email, case #, court, or staff"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <Button type="submit" variant="contained" size="small">
              Search
            </Button>
          </Box>

          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel id="intake-staff-filter-label">
              Assigned Staff
            </InputLabel>
            <Select
              labelId="intake-staff-filter-label"
              label="Assigned Staff"
              value={selectedStaff}
              onChange={(e) => setSelectedStaff(e.target.value)}
            >
              <MenuItem value="all">All staff</MenuItem>
              <MenuItem value="unassigned">Unassigned only</MenuItem>
              {(data?.staffOptions ?? []).map((staffName) => (
                <MenuItem key={staffName} value={staffName}>
                  {staffName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel id="intake-flag-filter-label">Flag Filter</InputLabel>
            <Select
              labelId="intake-flag-filter-label"
              label="Flag Filter"
              value={selectedFlag}
              onChange={(e) =>
                setSelectedFlag(e.target.value as IntakeFlagFilter)
              }
            >
              <MenuItem value="all">All open cases & leads</MenuItem>
              <MenuItem value="no_contact_2d">
                Flagged: LOR Not Sent, No Contact 2+ Days
              </MenuItem>
              <MenuItem value="lor_unconfirmed">
                Flagged: LOR Sent, Awaiting Court Confirmation
              </MenuItem>
              <MenuItem value="lor_not_sent">LOR not sent yet</MenuItem>
              <MenuItem value="lor_confirmed">LOR confirmed</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="intake-created-filter-label">
              Created Date
            </InputLabel>
            <Select
              labelId="intake-created-filter-label"
              label="Created Date"
              value={createdDays}
              onChange={(e) => setCreatedDays(Number(e.target.value))}
            >
              <MenuItem value={0}>All open cases</MenuItem>
              <MenuItem value={7}>Created in past 7 days</MenuItem>
              <MenuItem value={14}>Created in past 14 days</MenuItem>
              <MenuItem value={30}>Created in past 30 days</MenuItem>
              <MenuItem value={60}>Created in past 60 days</MenuItem>
            </Select>
          </FormControl>

          <Button size="small" variant="text" onClick={handleResetFilters}>
            Reset
          </Button>
        </Stack>
      </Paper>

      {/* Main Intake Case & Lead Tracking Table */}
      <TableContainer component={Paper} elevation={1} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Case / Lead & Court</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Assigned Staff</TableCell>
              <TableCell>Last Call or Note (Date & Who)</TableCell>
              <TableCell>Days Since Contact</TableCell>
              <TableCell>LOR Sent Date</TableCell>
              <TableCell>LOR Confirmed Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !data ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No cases or leads match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.rowKey}
                  hover
                  sx={{
                    bgcolor: row.flaggedNoContact2PlusDays
                      ? 'rgba(211, 47, 47, 0.03)'
                      : undefined,
                  }}
                >
                  {/* 1. Client */}
                  <TableCell>
                    {row.userGuid ? (
                      <Typography
                        variant="body2"
                        component={Link}
                        to={`/user?User_guid=${encodeURIComponent(row.userGuid)}`}
                        sx={{
                          fontWeight: 600,
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {row.clientName}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.clientName}
                      </Typography>
                    )}
                    {row.clientPhone && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {row.clientPhone}
                      </Typography>
                    )}
                    {row.clientEmail && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {row.clientEmail}
                      </Typography>
                    )}
                  </TableCell>

                  {/* 2. Case / Lead & Court */}
                  <TableCell>
                    {row.kind === 'lead' ? (
                      <Box>
                        <Chip
                          size="small"
                          color="secondary"
                          label={leadSourceLabel(row.leadSource)}
                          sx={{ mb: 0.5 }}
                        />
                        {row.courtName && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {row.courtName}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Box>
                        {row.ticketGuid ? (
                          <Typography
                            variant="body2"
                            component={Link}
                            to={`/ticket?Ticket_guid=${encodeURIComponent(row.ticketGuid)}`}
                            sx={{
                              fontWeight: 600,
                              color: 'primary.main',
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {row.caseNumber || `Case #${row.ticketId}`}
                          </Typography>
                        ) : (
                          <Typography variant="body2">
                            {row.caseNumber || '—'}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {row.courtName || 'No court assigned'}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>

                  {/* 3. Created Date */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2">
                      {formatDateShort(row.createdAt)}
                    </Typography>
                  </TableCell>

                  {/* 4. Assigned Staff */}
                  <TableCell>
                    {row.assignedStaffName ? (
                      <Box>
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={row.assignedStaffName}
                          onClick={() => setSelectedStaff(row.assignedStaffName!)}
                        />
                        {row.assignedStaffSource === 'last_contact' && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.25 }}
                          >
                            from last contact
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="Unassigned"
                        onClick={() => setSelectedStaff('unassigned')}
                      />
                    )}
                  </TableCell>

                  {/* 5. Last Call or Note (Date & Who) */}
                  <TableCell sx={{ maxWidth: 270 }}>
                    {row.lastContactAt ? (
                      <Box>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatDateTimeShort(row.lastContactAt)}
                          </Typography>
                          {row.lastContactKind && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={
                                row.lastContactKind === 'call'
                                  ? 'Call'
                                  : row.lastContactKind === 'message'
                                  ? 'Message'
                                  : 'Note'
                              }
                              sx={{ height: 18, fontSize: '0.68rem' }}
                            />
                          )}
                        </Stack>
                        <Typography
                          variant="caption"
                          color="text.primary"
                          sx={{ display: 'block', fontWeight: 500 }}
                        >
                          By: {row.lastContactBy || 'Unknown'}
                        </Typography>
                        {row.lastContactSummary && (
                          <Tooltip title={row.lastContactSummary}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 250,
                              }}
                            >
                              {row.lastContactSummary}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="error.main">
                        No call or note recorded
                      </Typography>
                    )}
                  </TableCell>

                  {/* 6. Days Since Last Contact */}
                  <TableCell>
                    {row.flaggedNoContact2PlusDays ? (
                      <Chip
                        size="small"
                        color="error"
                        label={`${row.daysSinceLastContact}d — Needs Contact`}
                      />
                    ) : (
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label={
                          row.daysSinceLastContact === 0
                            ? 'Today (0d)'
                            : `${row.daysSinceLastContact}d ago`
                        }
                      />
                    )}
                  </TableCell>

                  {/* 7. LOR Sent Date */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.kind === 'lead' ? (
                      <Typography variant="caption" color="text.secondary">
                        N/A (Lead)
                      </Typography>
                    ) : row.lorSentAt ? (
                      <Typography variant="body2">
                        {formatDateShort(row.lorSentAt)}
                      </Typography>
                    ) : row.flaggedLorNotSent ? (
                      <Chip
                        size="small"
                        color="default"
                        variant="outlined"
                        label="Not sent"
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>

                  {/* 8. LOR Confirmed Date */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.kind === 'lead' ? (
                      <Typography variant="caption" color="text.secondary">
                        N/A (Lead)
                      </Typography>
                    ) : row.lorConfirmedAt ? (
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label={`Confirmed ${formatDateShort(row.lorConfirmedAt)}`}
                      />
                    ) : row.flaggedLorUnconfirmed ? (
                      <Chip
                        size="small"
                        color="warning"
                        label={`Unconfirmed (${row.lorDaysSinceSent ?? 0}d)`}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 4 }} />

      {/* Embedded Intake Automation Activity Feed */}
      <AutomationActivityFeed
        defaultDepartment="intake"
        pageSize={25}
        title="Intake & LOR Automation Activity"
        subtitle="Letters of Representation, day 3/7/10 court follow-ups, welcome messages, payment links, and agreement reminders."
        compact
      />
    </Container>
  );
};

export default ManagerIntakeHomeView;
